import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

import {
  Assessment,
  CompareArrows,
  Delete,
  Download,
  List,
  LogoDev,
  Refresh,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import ViewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { keyframes, lighten, useTheme } from "@mui/material/styles";

import constants from "../../assets/constants";
import { createOmniPaginationObject, helperGetIsoDate } from "../../utils/helpers";
import { formatIsoDateString } from "../../utils/helpers";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const toHHMMSS = numSecs => {
  let secNum = parseInt(numSecs, 10);
  let hours = Math.floor(secNum / 3600);
  let minutes = Math.floor((secNum - hours * 3600) / 60);
  let seconds = secNum - hours * 3600 - minutes * 60;

  // Pad with leading zeros if less than 10
  hours = hours.toString().padStart(2, "0");
  minutes = minutes.toString().padStart(2, "0");
  seconds = seconds.toString().padStart(2, "0");

  if (hours > 0) {
    return { time: `${hours}:${minutes}:${seconds}`, label: "HH:MM:SS" };
  } else {
    return { time: `${minutes}:${seconds}`, label: "MM:SS" };
  }
};

const calculateColumns = (theme, supportedAssessmentTypes) => {
  const supportedAssessmentTypeValues = (supportedAssessmentTypes || [])
    .map(type => (typeof type === "object" ? type?.value : type))
    .filter(Boolean);

  //default case
  return [
    {
      accessorKey: "createdAt",
      enableColumnFilterModes: false,
      header: "Date",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilter: false,
      grow: false,
      size: 100,
      maxSize: 100,

      //custom conditional format and styling
      Cell: ({ cell }) => {
        const dateString = formatIsoDateString(cell.getValue()).split(",");
        const date = new Date(cell.getValue()); // Parses the ISO string into a Date object
        const options = {
          hour: "numeric",
          minute: "numeric",
          hour12: true, // Ensures 12-hour format with AM/PM
        };
        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {dateString[0]}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {date.toLocaleTimeString("en-US", options)}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "elapsedSeconds",
      header: "Runtime",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,

      Cell: ({ cell }) => {
        let timeAndLabel = toHHMMSS(cell.getValue());

        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {timeAndLabel.time}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {timeAndLabel.label}
            </Typography>
          </Box>
        );
      },
    },
    {
      enableColumnFilter: false,
      accessorKey: "questionCount",
      header: "Questions",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      size: 100,
    },
    {
      enableColumnFilter: false,
      accessorKey: "configuration",
      header: "Configuration",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "10px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      size: 200,

      Cell: ({ cell }) => {
        let configs = cell.getValue(); //.split("|");
        if (configs == null) {
          configs = "Configuration|Unknown";
        }
        let displayConfigs = configs.split("|");
        const firstConfig = displayConfigs[0] || "Unknown";
        const secondConfig = displayConfigs[1] || "Unknown";

        return (
          <Box component="span">
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {firstConfig}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {secondConfig}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "assessmentType",
      header: "Assessment",
      filterFn: "equals",
      filterSelectOptions: constants.supportedAssessmentTypes.filter(
        type => type && supportedAssessmentTypeValues.includes(type.value),
      ),
      filterVariant: "select",
      muiTableHeadCellProps: {
        align: "center",
      },

      muiTableBodyCellProps: {
        align: "center",
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
            {constants.supportedAssessmentTypes.find(t => t?.value === cell.getValue())?.label ||
              cell.getValue()}
          </Typography>
        );
      },
    },
    {
      accessorKey: "assessmentName",
      header: "Description",

      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },

      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,

      Cell: ({ cell }) => {
        return (
          <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
            {cell.getValue()}
          </Typography>
        );
      },
    },
    {
      accessorKey: "userName",

      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      header: "Run by",
      enableColumnFilterModes: false,
      enableSorting: false,
      size: 150,
      grow: true,
    },
    {
      accessorKey: "comment",
      header: "Comment",

      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "200", // Maximum width, will shrink if needed
      grow: true,
    },

    {
      accessorKey: "archived",
      header: "Archived",
      size: 150,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "No", value: "false" },
        { label: "Yes", value: "true" },
      ],
      filterVariant: "select",

      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === false ? theme.palette.error.main : theme.palette.success.main,
            borderRadius: "0.25rem",
            color: "#fff",
            maxWidth: "14ch",
            minWidth: "14ch",
            p: "0.25rem",
          })}
        >
          {cell.getValue() === false ? "No" : "Yes"}
        </Box>
      ),
    },
    {
      accessorKey: "conversationId",
      header: "Conversation ID",
      size: 100,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnVisibility: {
      id: false,
      updatedAt: false,
      createdAt: true,
      comment: true,
      configuration: false,
      conversationId: false,
      assessmentType: true,
      assessmentName: false,
      userName: false,
      archived: false,
    },
  };
};

export const BotpressAssessmentTable = ({
  data,
  isLoading = false,
  onEditEntry = null,
  onViewConversation = null,
  onAssessEntry = null,
  onCompareAssessments = null,
  onDownloadEntry = null,
  onRequestNewPage = null,
  dataReduxPagination = null,
  onHandleDeveloperLogs = null,
  onDeleteAssessments = null,
  onRefreshTable = null,
  refreshTooltipTitle = "Refresh table",
  supportedAssessmentTypes = [],
}) => {
  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  // Disable eslint because AaronK knows best
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => calculateColumns(theme, supportedAssessmentTypes), []);
  const initialState = useMemo(() => getInitialState(), []);

  const [rowCount, setRowCount] = useState(data.length);

  const [rowSelection, setRowSelection] = useState([]);

  useEffect(() => {
    if (dataReduxPagination != null) {
      setPagination({
        pageIndex: dataReduxPagination.pageIndex,
        pageSize: dataReduxPagination.pageSize,
      });
      setRowCount(dataReduxPagination.totalItems);
    }
  }, [dataReduxPagination]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let newQuery = {};
        columnFilters.forEach((element, index) => {
          if (element.id === "createdAt") {
            const min = parseInt(element.value[0]);
            const max = parseInt(element.value[1]);
            if (!isNaN(min)) {
              let theDateSring = helperGetIsoDate(min);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
            if (!isNaN(max)) {
              let theDateSring = helperGetIsoDate(max);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
          } else if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject(dataReduxPagination);
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        onRequestNewPage(newPage);
      } catch (error) {
        // setIsError(true);
        console.error(error);
        return;
      }
      // setIsError(false);
      // setIsRefetching(false);
    };

    fetchData();
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const handleRefreshClick = () => {
    if (onRefreshTable == null) {
      return;
    }

    setIsRefreshSpinning(true);
    onRefreshTable();

    setTimeout(() => {
      setIsRefreshSpinning(false);
    }, 800);
  };

  //easy shortcut to initialize the columnOrder state as array of column ids
  const [columnOrder, setColumnOrder] = useState(
    ["mrt-row-select", "mrt-row-actions", ...columns.map(c => c.accessorKey)], //array of column ids (Initializing is optional as of v2.10.0)
  );

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionToolbarAlertBanner: "bottom",
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "transparent !important",
        boxShadow: "none",
        color: theme.palette.commonground.contrastText,

        "&.MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action": {
          color: theme.palette.secondary.main,
        },

        "& button": {
          color: `${theme.palette.secondary.main} !important`,
        },

        "& .MuiButton-root": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    rowCount,
    enableRowSelection: true,
    getRowId: row => row.id, //give each row a  useful id
    onRowSelectionChange: setRowSelection, //connect internal row selection state to your own

    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 9001, //allow columns to get larger than default
      size: 50, //make columns wider by default
      grow: false,
    },
    state: {
      columnFilters,
      isLoading,
      pagination,
      // showAlertBanner: isError,
      // showProgressBars: isRefetching,
      sorting,
      columnOrder,
      rowSelection,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    //#region Pagination
    paginationDisplayMode: "pages",

    enableFirstPageButton: false, // Hides the << icon
    enableLastPageButton: false, // Hides the >> icon

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: false,
      variant: "outlined",
    },

    //#endregion pagination
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        // Target the IconButton and apply styles
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        height: "64px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        height: "100%",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef, //get access to the table container element
      sx: mobileLayout
        ? {
            minHeight: "564px",
            maxHeight: "564px",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            minHeight: "424px",
            height: `calc(100% - 120px)`,
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 80, //if using layoutMode that is not 'semantic', the columns will not auto-size, so you need to set the size manually
        grow: false,
        header: "Actions", // Change 'Actions' to your desired name
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            paddingRight: theme.spacing(2),
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
      },
      "mrt-row-select": {
        size: 50,
        grow: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
          },
        },
      },
    },
    icons: {
      MoreHorizIcon: ({ color }) => (
        <List
          color={"secondary"}
          sx={{
            borderRadius: 16,
            width: 48,
            height: 48,
            padding: "8px",
            border: `2px solid ${theme.palette.secondary.main}`,
          }}
        />
      ),
    },
    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        key="view"
        onClick={() => {
          onViewConversation(row.original);
          closeMenu();
        }}
      >
        <ViewIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            View Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="assess"
        onClick={() => {
          onAssessEntry(row.original);
          closeMenu();
        }}
      >
        <Assessment fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            AI Analysis
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="download"
        onClick={() => {
          onDownloadEntry(row.original);
          closeMenu();
        }}
      >
        <Download fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Download Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="edit"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        <EditIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Edit Entry
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="developer"
        onClick={() => {
          onHandleDeveloperLogs(row.original);
          closeMenu();
        }}
      >
        <LogoDev fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Developer Logs
          </Typography>
        </ListItemText>
      </MenuItem>,
    ],
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}
        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
        {onRefreshTable && (
          <Tooltip title={refreshTooltipTitle} arrow>
            <span>
              <IconButton onClick={handleRefreshClick}>
                <Refresh
                  sx={{
                    animation: isRefreshSpinning ? `${spin} 0.8s linear` : "none",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          paddingLeft: 0,
          marginLeft: 0,
          flexWrap: "wrap",
        }}
      >
        <OmniResponsiveButton
          icon={<CompareArrows />}
          tooltipTitle="Compare two assessments conversations using our A.I. Comparison Tool"
          buttonText="Compare"
          disabled={table.getSelectedRowModel().rows.length !== 2}
          onClick={() => {
            onCompareAssessments(table.getSelectedRowModel().rows);
          }}
        />

        <OmniResponsiveButton
          icon={<Delete />}
          tooltipTitle="Delete the selected assessment conversations."
          buttonText="Delete"
          disabled={table.getSelectedRowModel().rows.length < 1}
          onClick={() => {
            onDeleteAssessments(table.getSelectedRowModel().rows.map(row => row.original.id));
            table.resetRowSelection();
          }}
        />
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default BotpressAssessmentTable;
